// AudioManager.js
class AudioManager {
  constructor() {
    this.audioInstances = new Set();
    this.gainNodes = new Set();
    this.audioContexts = new Set();
    this.isMuted = localStorage.getItem("isMuted") === "true";
    this.isInitialized = false;
    this.listeners = new Set();

    console.log("🎵 AudioManager initialized, isMuted:", this.isMuted);

    this.overrideAudioConstructor();
    this.initOnUserGesture();
    this.startContinuousMuteCheck(); // Changed from startDOMObserver
  }

  addListener(callback) {
    this.listeners.add(callback);
    console.log("📢 Listener added, total listeners:", this.listeners.size);
  }

  removeListener(callback) {
    this.listeners.delete(callback);
    console.log("📢 Listener removed, total listeners:", this.listeners.size);
  }

  notifyListeners() {
    console.log("📣 Notifying", this.listeners.size, "listeners");
    this.listeners.forEach((callback) => {
      callback(this.isMuted);
    });
  }

  // Continuously enforce mute on all audio instances
  startContinuousMuteCheck() {
    console.log("👀 Starting continuous mute check");

    setInterval(() => {
      this.audioInstances.forEach((audio) => {
        if (
          audio.muted !== this.isMuted ||
          audio.volume !== (this.isMuted ? 0 : 1)
        ) {
          audio.muted = this.isMuted;
          audio.volume = this.isMuted ? 0 : 1;
          console.log("🔄 Corrected audio mute state");
        }
      });

      this.gainNodes.forEach((gainNode) => {
        if (gainNode.gain.value !== (this.isMuted ? 0 : 1)) {
          gainNode.gain.value = this.isMuted ? 0 : 1;
          console.log("🔄 Corrected gain node");
        }
      });

      // Also check DOM elements
      const mediaElements = document.querySelectorAll("audio, video");
      mediaElements.forEach((media) => {
        if (media.muted !== this.isMuted) {
          media.muted = this.isMuted;
          media.volume = this.isMuted ? 0 : 1;
        }
      });
    }, 100); // Check every 100ms
  }

  initOnUserGesture() {
    const init = () => {
      if (this.isInitialized) return;
      this.isInitialized = true;
      console.log("✅ Audio initialized on user gesture");

      this.audioContexts.forEach((ctx) => {
        if (ctx.state === "suspended") {
          ctx.resume().catch((e) => console.log("Resume failed:", e));
        }
      });

      this.setMuted(this.isMuted);

      document.removeEventListener("touchstart", init);
      document.removeEventListener("click", init);
    };

    document.addEventListener("touchstart", init, {
      once: true,
      passive: true,
    });
    document.addEventListener("click", init, { once: true });
  }

  registerAudioContext(audioContext) {
    this.audioContexts.add(audioContext);
    console.log("🎼 AudioContext registered, total:", this.audioContexts.size);

    if (audioContext.state === "suspended") {
      audioContext
        .resume()
        .catch((e) => console.log("Context resume failed:", e));
    }
  }

  overrideAudioConstructor() {
    const self = this;
    const OriginalAudio = window.Audio;

    window.Audio = function (src) {
      const audio = new OriginalAudio(src);
      console.log("🎵 New Audio() created:", src);
      self.registerAudio(audio);
      return audio;
    };

    window.Audio.prototype = OriginalAudio.prototype;
  }

  registerAudio(audio) {
    this.audioInstances.add(audio);
    audio.muted = this.isMuted;
    audio.volume = this.isMuted ? 0 : 1;
    console.log(
      "✅ Audio registered, total instances:",
      this.audioInstances.size
    );

    // Intercept play() method
    const originalPlay = audio.play.bind(audio);
    audio.play = () => {
      audio.muted = this.isMuted;
      audio.volume = this.isMuted ? 0 : 1;
      console.log("▶️ Audio.play() intercepted, mute state:", this.isMuted);
      return originalPlay();
    };

    const cleanup = () => {
      this.audioInstances.delete(audio);
      console.log("🗑️ Audio cleaned up, remaining:", this.audioInstances.size);
    };
    audio.addEventListener("ended", cleanup);
    audio.addEventListener("error", cleanup);
  }

  registerGainNode(gainNode) {
    this.gainNodes.add(gainNode);
    gainNode.gain.value = this.isMuted ? 0 : 1;
    console.log("🎚️ GainNode registered, total:", this.gainNodes.size);
  }

  unregisterGainNode(gainNode) {
    this.gainNodes.delete(gainNode);
    console.log("🎚️ GainNode unregistered, remaining:", this.gainNodes.size);
  }

  setMuted(muted) {
    console.log("🔇 setMuted called:", muted);
    this.isMuted = muted;
    localStorage.setItem("isMuted", muted);

    const applyMute = () => {
      // Mute gain nodes
      this.gainNodes.forEach((gainNode) => {
        try {
          gainNode.gain.value = muted ? 0 : 1;
        } catch (e) {
          console.log("Gain node error:", e);
        }
      });

      // Mute Audio() instances
      console.log(
        "🎵 Applying mute to",
        this.audioInstances.size,
        "audio instances"
      );
      this.audioInstances.forEach((audio) => {
        try {
          audio.muted = muted;
          audio.volume = muted ? 0 : 1;
        } catch (e) {
          console.log("Audio mute error:", e);
        }
      });

      // Mute DOM elements
      const mediaElements = document.querySelectorAll("audio, video");
      mediaElements.forEach((media) => {
        try {
          media.muted = muted;
          media.volume = muted ? 0 : 1;
        } catch (e) {
          console.log("Media mute error:", e);
        }
      });
    };

    // Apply mute multiple times to ensure it sticks
    applyMute();
    setTimeout(applyMute, 10);
    setTimeout(applyMute, 50);
    setTimeout(applyMute, 100);

    this.notifyListeners();

    console.log("✅ Mute state changed to:", muted);
  }

  getMuted() {
    return this.isMuted;
  }
}

const audioManager = new AudioManager();
export default audioManager;
